import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Typography,
  IconButton,
  TextField,
  Box,
  Tooltip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Fade,
  InputAdornment,
  Collapse,
  useScrollTrigger,
} from '@mui/material';
// SettingsBrightnessIcon removed (unused)
import DashboardIcon from "../../images/icon_dashboard.png";
import ActionIcon from "../../images/out.png";
import SettingIcon from "../../images/icon_setting_dashboard.png";
import ListIconImage from "../../images/icon_list.png";
import DatasourceIcon from "../../images/in.png";
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
// RenameIcon removed (unused)
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingDataflowList from '../SettingDataflowList';
import SettingDatasources from '../SettingDatasources';
import '../../i18n';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../Utils/SnackBarContext';
import CloseButton from '../Common/CloseButton';
// StorageIcon removed (unused)

// Memoized style constants
const STYLES = {
  paper: {
    borderRadius: 2,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    background: '#fff',
    padding: { xs: 1.5, md: 2 },
    boxSizing: 'border-box',
    width: '100%',
    overflow: 'hidden',
    mb: 1,
    border: '1px solid rgba(0,0,0,0.06)',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
    transition: 'box-shadow 0.2s ease',
    zIndex: 10, // Keep header above most content to avoid overlays
    // minHeight removed here to allow responsive / collapsed sizing
    '&:hover': {
      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)'
    }
  },
  stickyPaper: {
    position: 'sticky',
    top: 0,
    zIndex: 1100,
    borderRadius: 0,
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.12)',
    padding: { xs: 1, md: 1.5 },
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    minHeight: '80px', // Reserve minimum space for sticky mode
  },
  dialogTitle: {
    borderBottom: '1px solid rgba(0,0,0,0.1)',
    background: 'linear-gradient(to right, #5757b0, #8080d7)',
    color: 'white',
    fontWeight: 600 
  },
  expandButton: {
    backgroundColor: '#fff',
    border: '1px solid rgba(87, 87, 176, 0.4)',
    transition: 'all 0.2s ease',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    boxShadow: '0 6px 14px rgba(87, 87, 176, 0.25)',
    pointerEvents: 'auto',
    width: '36px',
    height: '36px',
    color: '#5757b0',
    '&:hover': {
      transform: 'translateX(-50%) scale(1.03)',
    },
    '& svg': {
      fontSize: '20px',
    }
  }
};

// Extracted control button component for reusability
const ControlButton = React.memo(({ title, onClick, icon, isSticky, active = false }) => (
  <Tooltip title={title} arrow placement="bottom">
    <IconButton 
      onClick={onClick}
      size="small"
      aria-pressed={active}
      sx={{
        borderRadius: 1,
        border: active ? '1px solid rgba(87,87,176,0.8)' : '1px solid rgba(87, 87, 176, 0.3)',
        width: isSticky ? 34 : 40,
        height: isSticky ? 34 : 40,
        backgroundColor: active ? 'rgba(87,87,176,0.15)' : '#fff',
        color: active ? '#373f8c' : 'inherit',
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: active ? 'rgba(87,87,176,0.25)' : 'rgba(87, 87, 176, 0.08)'
        }
      }}
    >
      {icon}
    </IconButton>
  </Tooltip>
));

// Helper function for responsive icon styling
const getIconStyle = (isSticky, useTheme = false) => {
  const size = isSticky ? '20px' : '24px'; // Fixed sizes to prevent layout shift
  
  return {
    width: size,
    height: size,
    display: 'block', // Prevent inline spacing issues
    objectFit: 'contain', // Maintain aspect ratio
    transition: 'all 0.2s ease'
  };
};

const DashboardHeader = React.memo(({
  title,
  setTitle,
  description,
  setDescription,
  folder,
  setVisibleSection,
  openFolder,
  ws,
  caseid,
  tags,
  onDatasourceClick,
  visibleSection,
  isReadOnly,
  autoOpenDatasource,
  onDatasourceOpened,
  autoOpenActions,
  autoOpenExportId,
  onActionsOpened
}) => {
  
  const { openSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [showRename, setShowRename] = useState(false);  const [expanded, setExpanded] = useState(true);
  const [showDatasource, setShowDatasource] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  
  // Auto-open datasource modal after OAuth redirect
  useEffect(() => {
    if (autoOpenDatasource && !showDatasource) {
      setShowDatasource(true);
      if (onDatasourceOpened) {
        onDatasourceOpened();
      }
    }
  }, [autoOpenDatasource, showDatasource, onDatasourceOpened]);

  // Auto-open actions modal after OAuth redirect
  useEffect(() => {
    if (autoOpenActions && !showRename) {
      setShowRename(true);
      if (onActionsOpened) {
        onActionsOpened();
      }
    }
  }, [autoOpenActions, showRename, onActionsOpened]);
  
  // Use two different thresholds to create hysteresis and prevent flickering
  const enterStickyTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100, // Threshold to enter sticky mode
  });
  
  const exitStickyTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 70, // Lower threshold to exit sticky mode (creates hysteresis)
  });

  // Update sticky state based on scrolling with hysteresis to prevent flickering
  useEffect(() => {
    if (enterStickyTrigger && !isSticky) {
      // Entering sticky mode
      setExpanded(false); // Auto-collapse when scrolling
      setIsSticky(true);
    } else if (!exitStickyTrigger && isSticky) {
      // Exiting sticky mode - only when scrolling well above the threshold
      setIsSticky(false);
    }
  }, [enterStickyTrigger, exitStickyTrigger, isSticky]);

  // Local state for title and description
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);

  const handleTitleChange = useCallback((e) => setLocalTitle(e.target.value), []);
  const handleDescriptionChange = useCallback((e) => setLocalDescription(e.target.value), []);

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    setTitle(localTitle);
    ws.send(JSON.stringify({ command: 'changetitle', caseid, title: localTitle }));
    openSnackbar("Summary updated successfully", 3000);
  }, [ws, caseid, localTitle, setTitle, openSnackbar]);

  const handleDescriptionBlur = useCallback(() => {
    setIsEditingDescription(false);
    setDescription(localDescription);
    ws.send(JSON.stringify({ command: 'changedescription', caseid, description: localDescription }));
    openSnackbar("Summary updated successfully", 3000);
  }, [ws, caseid, localDescription, setDescription, openSnackbar]);

  const handleTitleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleTitleBlur();
    } else if (e.key === 'Escape') {
      setLocalTitle(title); // Reset to original title
      setIsEditingTitle(false);
    }
  }, [handleTitleBlur, title]);

  const handleDescriptionKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleDescriptionBlur();
    } else if (e.key === 'Escape') {
      setLocalDescription(description); // Reset to original description
      setIsEditingDescription(false);
    }
  }, [handleDescriptionBlur, description]);

  // Memoized handlers
  const handleSetVisibleSection = useCallback(
    (section) => () => setVisibleSection(section), 
    [setVisibleSection]
  );
  
  const handleEditTitle = useCallback(() => {
    if (!isReadOnly) {
      setIsEditingTitle(true);
    }
  }, [isReadOnly]);

  const handleEditDescription = useCallback(() => {
    if (!isReadOnly) {
      setIsEditingDescription(true);
    }
  }, [isReadOnly]);

  const handleToggleRename = useCallback((show) => () => {
    setShowRename(show);
  }, []);

  const handleToggleDatasource = useCallback((show) => () => {
    setShowDatasource(show);
  }, []);

  const toggleExpanded = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  // Sync local state with props efficiently - only when not editing
  useEffect(() => {
    if (!isEditingTitle && title !== localTitle) {
      setLocalTitle(title);
    }
  }, [title, localTitle, isEditingTitle]);

  useEffect(() => {
    if (!isEditingDescription && description !== localDescription) {
      setLocalDescription(description);
    }
  }, [description, localDescription, isEditingDescription]);

  const isTitlePlaceholder = !(localTitle && localTitle.trim());
  const isDescriptionPlaceholder = !(localDescription && localDescription.trim());

  // Memoized complex UI elements
  const titleSection = useMemo(() => (
    <Typography 
      variant="h5" 
      sx={{ 
        width: '100%',
        textAlign: 'center',
        fontWeight: 600,
        color: isTitlePlaceholder ? '#94a3b8' : '#1f2432',
        position: 'relative',
        transition: 'color 0.2s ease',
        cursor: isEditingTitle ? 'default' : 'pointer',
        fontSize: { xs: isSticky ? '1.2rem' : '1.4rem', sm: isSticky ? '1.3rem' : '1.7rem', md: isSticky ? '1.4rem' : '1.9rem' },
        lineHeight: 1.2,
        wordBreak: 'break-word',
        '&:hover': !isEditingTitle ? {
          color: '#5757b0',
        } : {}
      }}
    >
      {isEditingTitle ? (
        <TextField
          value={localTitle}
          onChange={handleTitleChange}
          onBlur={handleTitleBlur}
          onKeyDown={handleTitleKeyDown}
          autoFocus
          fullWidth
          variant="standard"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EditIcon fontSize="small" color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: isSticky ? '1.3rem' : '1.8rem',
              fontWeight: 600,
              textAlign: 'center'
            }
          }}
        />
      ) : (
        <Box 
          onClick={handleEditTitle} 
          sx={{ 
            display: 'inline-flex', 
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            '&:hover .editIcon': {
              opacity: 1,
              transform: 'translateX(0)'
            }
          }}
        >
          {isTitlePlaceholder ? t('dashboard.add_title', 'Add title') : localTitle}
          <EditIcon 
            className="editIcon"
            fontSize="small" 
            sx={{ 
              ml: 1, 
              opacity: 0, 
              transform: 'translateX(-10px)',
              transition: 'all 0.2s ease',
              color: '#5757b0',
              fontSize: isSticky ? '0.8rem' : '1rem' // Smaller icon when sticky
            }} 
          />
        </Box>
      )}
    </Typography>
  ), [isEditingTitle, localTitle, handleTitleChange, handleTitleBlur, handleTitleKeyDown, handleEditTitle, isSticky, isTitlePlaceholder, t]);

  const descriptionSection = useMemo(() => (
    <Box sx={{ 
      my: 2, 
      width: '100%', 
      textAlign: 'center',
      minHeight: '38px', // Reserve space to prevent layout shift (smaller)
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {isEditingDescription ? (
        <TextField
          value={localDescription}
          onChange={handleDescriptionChange}
          onBlur={handleDescriptionBlur}
          onKeyDown={handleDescriptionKeyDown}
          multiline
          fullWidth
          variant="outlined"
          label={t('dashboard.description_label')}
          rows={3}
          autoFocus
          InputProps={{
            sx: {
              textAlign: 'center'
            }
          }}
        />
      ) : (
        <Typography 
          variant="body1" 
          onClick={handleEditDescription}
          sx={{
            cursor: 'pointer',
            color: isDescriptionPlaceholder ? '#94a3b8' : '#4a5568',
            borderRadius: 1,
            padding: 0,
            textAlign: 'center',
            minHeight: '24px', // Reserve minimum height for text
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            whiteSpace: 'pre-line', // Preserve line breaks in description
            wordBreak: 'break-word',
            '&:hover': {
              color: '#2c3e50'
            }
          }}
        >
          {isDescriptionPlaceholder ? t('dashboard.add_description', 'Add description') : localDescription}
        </Typography>
      )}
    </Box>
  ), [isEditingDescription, localDescription, handleDescriptionChange, handleDescriptionBlur, handleDescriptionKeyDown, handleEditDescription, isDescriptionPlaceholder, t]);

  const controlButtons = useMemo(() => {
    if (isReadOnly) return null;

    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <ControlButton
          title={t('dashboard.view_files_timeline')}
          onClick={handleSetVisibleSection("timeline")}
          isSticky={isSticky}
          active={visibleSection === "timeline"}
          icon={<img src={DashboardIcon} alt="Timeline View Icon" style={getIconStyle(isSticky)} />}
        />
        <ControlButton
          title={t('dashboard.view_event_list')}
          onClick={handleSetVisibleSection("eventlist")}
          isSticky={isSticky}
          active={visibleSection === "eventlist"}
          icon={<img src={ListIconImage} alt="Event List Icon" style={getIconStyle(isSticky)} />}
        />
        <ControlButton
          title={t('dataconnectors.title') || 'Data Source'}
          onClick={handleToggleDatasource(true)}
          isSticky={isSticky}
          icon={<img src={DatasourceIcon} alt="Datasource Icon" style={getIconStyle(isSticky)} />}
        />
        <ControlButton
          title={t('settings.title_dataflow')}
          onClick={handleToggleRename(true)}
          isSticky={isSticky}
          icon={<img src={ActionIcon} alt="Action Icon" style={getIconStyle(isSticky)} />}
        />
        <ControlButton
          title={t('dashboard.settings')}
          onClick={handleSetVisibleSection("settings")}
          isSticky={isSticky}
          active={visibleSection === "settings"}
          icon={<img src={SettingIcon} alt="Settings Icon" style={getIconStyle(isSticky)} />}
        />
      </Box>
    );
  }, [isReadOnly, isSticky, t, handleSetVisibleSection, handleToggleDatasource, handleToggleRename, visibleSection]);

  const datasourceDialog = useMemo(() => showDatasource && (
    <Dialog
      open={showDatasource}
      onClose={handleToggleDatasource(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          width: '90%',
          maxWidth: '900px',
        }
      }}
    >
      <DialogTitle sx={{
        ...STYLES.dialogTitle,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pr: 1
      }}>
        {t('dataconnectors.title') || 'Datasource'}
        <CloseButton
          ariaLabel="close"
          onClick={handleToggleDatasource(false)}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        />
      </DialogTitle>
      <DialogContent sx={{ mt: { xs: 1, sm: 2 }, p: { xs: 1, sm: 3 } }}>
          <SettingDatasources 
            caseid={caseid} 
            hideHeader={true} 
            onClose={handleToggleDatasource(false)} 
            autoOpenConnector={autoOpenDatasource}
            onConnectorOpened={onDatasourceOpened}
          />
      </DialogContent>
    </Dialog>
  ), [showDatasource, handleToggleDatasource, t, caseid, autoOpenDatasource, onDatasourceOpened]);

  // Lazy load dialog only when needed
  const createFileDialog = useMemo(() => showRename && (
    <Dialog 
      open={showRename} 
      onClose={handleToggleRename(false)} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          width: '90%',
          maxWidth: '900px', // Increase max width for larger dialog
        }
      }}
    >      <DialogTitle sx={{
        ...STYLES.dialogTitle,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pr: 1
      }}>
        {t('dashboard.rename_files')}
        <CloseButton
          ariaLabel="close"
          onClick={handleToggleRename(false)}
          sx={{
            color: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        />
      </DialogTitle>
      <DialogContent sx={{ mt: 1, p: { xs: 1, sm: 3 } }}>        
        <SettingDataflowList 
          caseid={caseid} 
          onClose={handleToggleRename(false)}
          autoOpenExportId={autoOpenExportId}
          onExportOpened={onActionsOpened}
        />
      </DialogContent>
    </Dialog>
  ), [showRename, handleToggleRename, caseid, t, autoOpenExportId, onActionsOpened]);

  // Folder section component
  const folderSection = useMemo(() => (
    folder && folder.trim() !== "" ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
        <Tooltip title={t('dashboard.open_folder')} arrow>
          <IconButton 
            onClick={openFolder}
            size="small"
            sx={{
              border: '1px solid rgba(87, 87, 176, 0.3)',
              color: '#5757b0',
              '&:hover': {
                backgroundColor: 'rgba(87, 87, 176, 0.08)'
              }
            }}
          >
            <FolderOpenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography 
          variant="caption" 
          color="textSecondary"
          sx={{
            fontFamily: 'monospace',
            maxWidth: { xs: '100%', md: '320px' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {folder}
        </Typography>
      </Box>
    ) : null
  ), [folder, openFolder, t]);

  const expandButtonLabel = expanded 
    ? t('dashboard.collapse_header') || 'Collapse header' 
    : t('dashboard.expand_header') || 'Expand header';

  return (
    <Fade in={true} timeout={800}>
      <Paper elevation={isSticky ? 4 : 3} sx={{
          ...STYLES.paper,
          ...(isSticky ? STYLES.stickyPaper : {}),
          position: isSticky ? 'sticky' : 'relative',
        py: expanded ? 2 : 1,
        pb: expanded ? 2 : 1,
          minHeight: isSticky ? STYLES.stickyPaper.minHeight : (expanded ? '96px' : '56px'),
          overflow: 'visible',
          contain: 'layout', // Improve layout performance
          willChange: isSticky ? 'position, transform' : 'auto', // Optimize sticky transitions
        }}>
        <Container maxWidth={isSticky ? "xl" : "lg"} sx={{ 
          px: isSticky ? { xs: 1, sm: 2, md: 3 } : { xs: 1, sm: 2, md: 3 }, // Reduced responsive padding to reduce header vertical footprint
          overflow: 'visible', // Allow buttons to be visible
          position: 'relative', // Establish positioning context
          width: '100%'
        }}>
          <Box sx={{ position: 'relative', maxWidth: '100%', overflow: 'visible' }}>
            {/* Control buttons positioned absolutely on the right */}
            <Box sx={{ 
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 10,
              display: { xs: 'none', md: 'flex' }, // Hide on mobile, show on desktop
              minHeight: '40px', // Reserve space for buttons
              minWidth: '220px', // Reserve space for button row
            }}>
              {controlButtons}
            </Box>

            {/* Centered title and description */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0,
              mb: expanded && !isSticky ? 1 : 0.5,
              pr: { xs: 0, md: '280px' }, // Add right padding to prevent overlap with buttons on desktop
              pl: { xs: 0, md: '280px' }, // Add left padding to keep title centered
            }}>
              <Box sx={{ width: '100%', maxWidth: '100%' }}>
                {titleSection}
              </Box>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                {folderSection}
              </Box>
            </Box>
            
            {/* Mobile control buttons - centered below title */}
            <Box sx={{ 
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              mt: 1,
              mb: 1,
              minHeight: '40px', // Reserve space for buttons to prevent shift
            }}>
              {controlButtons}
            </Box>

            <Collapse in={expanded} timeout="auto">
              {descriptionSection}
            </Collapse>
          </Box>
        </Container>
        
        {/* Toggle expand/collapse button - hide when sticky */}
        {!isSticky && (
          <Box
            sx={{
              position: 'absolute',
              bottom: -6,
              left: 0,
              width: '100%',
              height: 20,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
                zIndex: 2000,
                pointerEvents: 'auto',
              willChange: 'transform', // Optimize rendering
            }}
          >
            <Tooltip title={expandButtonLabel} arrow>
              <IconButton 
                size="small" 
                onClick={toggleExpanded} 
                sx={STYLES.expandButton}
                aria-expanded={expanded}
                aria-label={expandButtonLabel}
              >
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
        
  {createFileDialog}
  {datasourceDialog}
      </Paper>
    </Fade>
  );
});

export default DashboardHeader;
